import React, { useCallback, useEffect, useRef, useState } from 'react'

export interface TurboFormModalData {
  embed_code: string
  bg_color?: string
}

const TRIGGER_SELECTOR = 'a[href*="#pv-open-12-days-of-turbo-popup"]'

const TurboFormModal: React.FC<TurboFormModalData> = ({ embed_code, bg_color }) => {
  const [hasOpened, setHasOpened] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  const openModal = useCallback(() => {
    setHasOpened(true)
    setIsOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    const handleTriggerClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null
      const trigger = target?.closest(TRIGGER_SELECTOR) as HTMLAnchorElement | null

      if (!trigger) return

      event.preventDefault()
      openModal()
    }

    document.addEventListener('click', handleTriggerClick)
    return () => {
      document.removeEventListener('click', handleTriggerClick)
    }
  }, [openModal])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeModal()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.body.classList.add('overflow-hidden')

    const focusNextTick = window.requestAnimationFrame(() => {
      const focusable = dialogRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      focusable?.focus()
    })

    return () => {
      window.cancelAnimationFrame(focusNextTick)
      document.removeEventListener('keydown', handleKeyDown)
      document.body.classList.remove('overflow-hidden')
    }
  }, [closeModal, isOpen])

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        closeModal()
      }
    },
    [closeModal],
  )

  if (!isOpen && !hasOpened) {
    return null
  }

  const overlayVisibilityClass = isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'

  return (
    <div
      className={`fixed inset-0 z-[999] flex items-center justify-center px-5 bg-[rgba(0,0,0,0.80)] transition-opacity duration-200 ease-out ${overlayVisibilityClass}`}
      role="presentation"
      onClick={handleOverlayClick}
      aria-hidden={!isOpen}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        className="turbo-embed-form relative z-10 w-full max-w-[535px] rounded bg-white py-4 lg:py-8 px-4 lg:px-24"
        style={bg_color ? { background: bg_color } : undefined}
      >
        {embed_code ? (
          <div>
            <div dangerouslySetInnerHTML={{ __html: embed_code }} />

            <div className="text-center">
              <button
                type="button"
                className="text-[16px] underline"
                aria-label="Nevermind, I'm good."
                onClick={closeModal}
              >
                <span>Nevermind, I'm good.</span>
              </button>
            </div>
          </div>
        ) : (
          <form>
            <div className="mb-3.5">
              <label htmlFor="first_name">First Name</label>
              <input type="text" placeholder="First Name" id="first_name" name="first_name" />
            </div>
            <div className="mb-3.5">
              <label htmlFor="last_name">Last Name</label>
              <input type="text" placeholder="Last Name" id="last_name" name="last_name" />
            </div>
            <div className="mb-3.5">
              <label htmlFor="email">Email</label>
              <input type="email" placeholder="Email" id="email" name="email" />
            </div>
            <div className="mb-3.5">
              <label htmlFor="phone_number">Phone Number</label>
              <input type="tel" placeholder="Phone Number" id="phone_number" name="phone_number" />
            </div>
            <div className="mb-3.5">
              <label htmlFor="address">Address</label>
              <input type="text" placeholder="Address" id="address" name="address" />
            </div>

            <div className="mt-9 text-center">
              <button type="button" className="button button--primary mb-8">
                <div className="rectangle-1" />
                <div className="button-text">Enter to Win!</div>
              </button>

              <button
                type="button"
                className="text-[16px] underline"
                aria-label="Nevermind, I'm good."
                onClick={closeModal}
              >
                <span>Nevermind, I'm good.</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default TurboFormModal
